import { Button, InlineNotification, PasswordInput } from '@carbon/react';
import { Password } from '@carbon/icons-react';
import { FormModal, problemMessage, type RecordValues } from '@pos/admin-ui';
import type { ApiProblem } from '@pos/api-client';
import { useTranslation } from '@pos/i18n';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi } from '../api';

const MIN_LENGTH = 8;

/** Sets a staff member's password at HO (ADR 0003); it reaches stores through sync. */
export function SetPasswordButton({ staff }: { staff: RecordValues }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState(false);
  const [done, setDone] = useState(false);

  const save = useMutation({
    mutationFn: async () => {
      const { error: problem, response } = await hoApi.PUT('/api/v1/identity/staff/{id}/password', {
        params: { path: { id: staff.id } },
        body: { password },
      });
      if (!response.ok) throw (problem ?? {}) as ApiProblem;
    },
    onSuccess: () => {
      setDone(true);
      close();
    },
  });

  function close() {
    setOpen(false);
    setPassword('');
    setTouched(false);
    save.reset();
  }

  // A message the server gave for the password field shows on the field; anything else is a general error.
  const problem = save.error as ApiProblem | null;
  const fieldError = problem?.errors ? Object.values(problem.errors)[0]?.[0] : undefined;
  const clientError =
    password.length === 0
      ? t('admin.validation.required')
      : password.length < MIN_LENGTH
        ? t('admin.passwordTooShort')
        : undefined;
  const shownError = (touched ? clientError : undefined) ?? fieldError;

  return (
    <>
      <Button kind="tertiary" renderIcon={Password} onClick={() => (setDone(false), setOpen(true))}>
        {t('admin.setPassword')}
      </Button>
      {done && (
        <InlineNotification
          kind="success"
          role="status"
          lowContrast
          title={t('admin.passwordSet')}
          onClose={() => setDone(false)}
        />
      )}
      {open && (
        <FormModal
          title={t('admin.setPassword')}
          label={String(staff['displayName'] ?? '')}
          size="sm"
          pending={save.isPending}
          error={save.error && !fieldError ? problemMessage(t, problem) : null}
          onSubmit={() => {
            setTouched(true);
            if (!clientError) save.mutate();
          }}
          onClose={close}
        >
          <PasswordInput
            id="new-password"
            labelText={t('admin.newPassword')}
            helperText={t('admin.passwordHint')}
            showPasswordLabel={t('carbon.showPassword')}
            hidePasswordLabel={t('carbon.hidePassword')}
            autoComplete="new-password"
            required
            value={password}
            invalid={Boolean(shownError)}
            invalidText={shownError}
            onBlur={() => setTouched(true)}
            onChange={(e) => {
              setPassword(e.target.value);
              if (save.error) save.reset();
            }}
          />
        </FormModal>
      )}
    </>
  );
}
